import React, { useEffect, useRef, useState } from 'react'
import { effectiveWindow, staleRangeLabel } from './tracpatch.window'
import { connect } from 'react-redux'
import { Row, Button } from 'antd'
import * as d3 from 'd3'
import moment from 'moment'
import '../../../css/patient.scss'
import { CHART_COLORS } from '../../../util/chartPalette'
import { DataViewToggle, ChartDataGrid } from '../../graph/chartDataView'
import { styleAxisTicks, AXIS_TITLE_FONT_SIZE, AXIS_TITLE_FONT_WEIGHT } from './tracpatch.chart.style'

// Per-day % of prescribed reps completed for a single TracPatch exercise.
//
// Daily target  = requiredReps × requiredSets  (the prescription for that
//                 day — taken from the first valid set of the day; we don't
//                 expect the prescription to change mid-day).
// Daily actual  = sum(repsCompleted) across EVERY set of the day (including
//                 overflow sets past the prescribed set count — they still
//                 count toward reps).
// Bar height    = min(actual / target, 1.0) × 100%  — display caps at 100%.
// Bar label     = the raw actual rep count, drawn above the bar.
//
// Same window controls (7 / 30 / 90 days) as the angle chart it sits next
// to. Days with no sets are simply absent from the x-axis.
//
// The peak-angle error filter is deliberately NOT applied here. It judges a set
// by its peak flexion/extension, which is the right test for the angle chart
// next door but says nothing about how many reps the patient did. Sets carrying
// real reps with missing or degenerate peak angles were being discarded, so an
// exercise the patient finished in full showed no bar at all.

const TracPatchExerciseRepsChart = (props) => {
  const svgRef = useRef(null)
  const containerRef = useRef(null)
  const tooltipRef = useRef(null)
  const [days, setDays] = useState(30)
  const [view, setView] = useState('chart')

  useEffect(() => {
    if (view === 'chart') draw()
  }, [props.data, days, view])

  const getDailyReps = () => {
    if (!props.data || props.data.length === 0) return []

    // Falls back to the patient's latest session — see tracpatch.window.js.
    const win = effectiveWindow(props.data, days, (d) => d && d.completedAt)
    const windowStart = win.windowStart.clone()
    const filterEnd = moment().endOf('day')

    const byDay = {}
    props.data.forEach((d) => {
      if (!d.completedAt) return
      const m = moment(d.completedAt)
      if (!m.isSameOrAfter(windowStart) || !m.isSameOrBefore(filterEnd)) return
      const key = m.format('YYYY-MM-DD')
      if (!byDay[key]) {
        // Prescription snapshot from the first set of the day. Both
        // fields default to 0 so the math stays defined; the bar renders
        // as "—" when the prescription is missing.
        byDay[key] = {
          date: m.clone().startOf('day').toDate(),
          dateLabel: m.format('MM/DD'),
          // Actual timestamp of the day's last completed set, for the grid.
          time: d.completedAt,
          repsDone: 0,
          requiredReps: d.requiredReps || 0,
          requiredSets: d.requiredSets || 0,
        }
      }
      if (m.isAfter(moment(byDay[key].time))) byDay[key].time = d.completedAt
      byDay[key].repsDone += d.repsCompleted || 0
    })

    return Object.values(byDay).sort((a, b) => a.date - b.date)
  }

  const draw = () => {
    const dailyData = getDailyReps()
    if (dailyData.length === 0) return

    const margin = { top: 32, right: 20, bottom: 60, left: 50 }
    const screenWidth = containerRef.current ? containerRef.current.offsetWidth : 400
    const screenHeight = 300
    const width = screenWidth - margin.left - margin.right
    const height = screenHeight - margin.top - margin.bottom

    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()

    const g = svg
      .attr('width', screenWidth)
      .attr('height', screenHeight)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`)

    const x = d3.scaleBand()
      .domain(dailyData.map((d) => d.dateLabel))
      .range([0, width])
      .padding(0.25)

    // Y axis fixed 0..100% — extras render at 100% with the actual rep
    // count on top so the over-achievement is still visible.
    const y = d3.scaleLinear().domain([0, 100]).range([height, 0])

    g.append('g')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(x))
      .selectAll('text')
      .style('text-anchor', 'end')
      .attr('dx', '-.8em')
      .attr('dy', '.15em')
      .attr('transform', 'rotate(-45)')
      .call(styleAxisTicks, dailyData.length > 20 ? 9 : 11)
      .style('fill', 'black')
      .style('stroke', 'none')

    g.append('text')
      .attr('text-anchor', 'end')
      .attr('x', width / 2 + margin.left)
      .attr('y', height + margin.bottom - 5)
      .attr('stroke', 'none')
      .attr('fill', 'black')
      .style('font-size', AXIS_TITLE_FONT_SIZE)
      .style('font-weight', AXIS_TITLE_FONT_WEIGHT)
      .text('Date')

    g.append('g')
      .call(d3.axisLeft(y).ticks(5).tickFormat((t) => t + '%'))
      .selectAll('text')
      .style('fill', 'black')
      .style('stroke', 'none')
      .call(styleAxisTicks, 10)

    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', -margin.left + 12)
      .attr('x', -height / 2)
      .attr('text-anchor', 'middle')
      .attr('fill', 'black')
      .attr('stroke', 'none')
      .style('font-size', AXIS_TITLE_FONT_SIZE)
      .style('font-weight', AXIS_TITLE_FONT_WEIGHT)
      .text('% reps completed')

    // Surgery-date marker
    const surgeryRaw = props.patient?.surgery?.date
    if (surgeryRaw) {
      const sd = moment(surgeryRaw)
      if (sd.isValid()) {
        const surgeryKey = sd.format('MM/DD')
        if (x(surgeryKey) != null) {
          const sx = x(surgeryKey) + x.bandwidth() / 2
          g.append('line')
            .attr('x1', sx)
            .attr('x2', sx)
            .attr('y1', 0)
            .attr('y2', height)
            .attr('stroke', '#000')
            .attr('stroke-width', 1.5)
            .attr('stroke-dasharray', '4,3')
          const labelText = 'Surgery ' + sd.format('M/D')
          const labelOnLeft = sx > width - 80
          g.append('text')
            .attr('x', sx + (labelOnLeft ? -4 : 4))
            .attr('y', -8)
            .attr('text-anchor', labelOnLeft ? 'end' : 'start')
            .style('font-size', '11px')
            .style('font-weight', '600')
            .style('fill', '#000')
            .text(labelText)
        }
      }
    }

    const tooltip = d3.select(tooltipRef.current)

    const pctFor = (d) => {
      const target = (d.requiredReps || 0) * (d.requiredSets || 0)
      if (target <= 0) return 0
      return Math.min(100, (d.repsDone / target) * 100)
    }
    const rawPctFor = (d) => {
      const target = (d.requiredReps || 0) * (d.requiredSets || 0)
      if (target <= 0) return null
      return (d.repsDone / target) * 100
    }

    // Bars — fixed prescribed-cap (100%) so over-performance doesn't
    // dominate the y-axis. Color: blue when on-track, green at ≥100%.
    g.selectAll('.rep-bar')
      .data(dailyData)
      .enter()
      .append('rect')
      .attr('class', 'rep-bar')
      .attr('x', (d) => x(d.dateLabel))
      .attr('y', (d) => y(pctFor(d)))
      .attr('width', x.bandwidth())
      .attr('height', (d) => height - y(pctFor(d)))
      .attr('fill', (d) => {
        const raw = rawPctFor(d)
        if (raw == null) return '#bdbdbd'
        return raw >= 100 ? CHART_COLORS.completeGreen : CHART_COLORS.lineBlue
      })
      .style('cursor', 'pointer')
      .on('mouseover', function (d) {
        const target = (d.requiredReps || 0) * (d.requiredSets || 0)
        const raw = rawPctFor(d)
        tooltip
          .style('visibility', 'visible')
          .html(
            `<b>Date</b>: ${d.dateLabel}` +
            `<br/><b>Reps</b>: ${d.repsDone}${target > 0 ? '/' + target : ''}` +
            `<br/><b>%</b>: ${raw == null ? '—' : Math.round(raw) + '%'}`
          )
          .style('top', d3.select(this).attr('y') - 42 + 'px')
          .style('left', parseInt(d3.select(this).attr('x')) + 10 + 'px')
      })
      .on('mouseout', () => tooltip.style('visibility', 'hidden'))

    // Reps count labels on top of each bar (the user's ask: "display the
    // number of reps for that exercise done on top of the bar").
    g.selectAll('.rep-label')
      .data(dailyData)
      .enter()
      .append('text')
      .attr('class', 'rep-label')
      .attr('x', (d) => x(d.dateLabel) + x.bandwidth() / 2)
      .attr('y', (d) => y(pctFor(d)) - 3)
      .attr('text-anchor', 'middle')
      .style('font-size', dailyData.length > 20 ? '9px' : '10px')
      .style('font-weight', '600')
      .style('fill', '#333')
      .style('pointer-events', 'none')
      .text((d) => String(d.repsDone))
  }

  const dailyData = getDailyReps()
  const gridRows = dailyData.map((d) => {
    const target = (d.requiredReps || 0) * (d.requiredSets || 0)
    const raw = target > 0 ? (d.repsDone / target) * 100 : null
    return {
      date: d.time || d.date,
      reps: target > 0 ? `${d.repsDone}/${target}` : `${d.repsDone}`,
      pct: raw == null ? '—' : Math.round(raw) + '%',
    }
  })
  const gridColumns = [
    { title: 'Reps', dataIndex: 'reps', key: 'reps' },
    { title: '% Completed', dataIndex: 'pct', key: 'pct' },
  ]

  // Same inputs the chart uses, so banner and chart agree.
  const stale = effectiveWindow(props.data, days, (d) => d && d.completedAt)

  return (
    <>
      <center>
        <h4>{(props.exerciseName ? props.exerciseName + ' — ' : '') + '% reps completed'}</h4>
      </center>
      {stale.isStale && (
        <Row justify='start' style={{ marginBottom: 8, paddingLeft: 12 }}>
          <div style={{ background:'#fff8e1', border:'1px solid var(--orange-color, #f5881f)',
                        borderRadius:8, padding:'6px 12px', fontSize:12, color:'#8a4b00', maxWidth:'100%' }}>
            {staleRangeLabel(stale.windowStart, stale.windowEnd, days)}
          </div>
        </Row>
      )}
      <Row justify='start' className='chartControls' style={{ marginBottom: 8, paddingLeft: 12 }}>
        <Button.Group size='small'>
          <Button type={days === 7 ? 'primary' : 'default'} onClick={() => setDays(7)}>7 Days</Button>
          <Button type={days === 30 ? 'primary' : 'default'} onClick={() => setDays(30)}>30 Days</Button>
          <Button type={days === 90 ? 'primary' : 'default'} onClick={() => setDays(90)}>90 Days</Button>
        </Button.Group>
        <DataViewToggle
          active={view === 'data'}
          onClick={() => setView(view === 'data' ? 'chart' : 'data')}
        />
      </Row>
      {dailyData.length > 0 ? (
        view === 'data' ? (
          <ChartDataGrid rows={gridRows} valueColumns={gridColumns} csvName='tracpatch-exercise-reps' />
        ) : (
          <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
            <div ref={tooltipRef} className='chartToolTip' style={{ position: 'absolute', visibility: 'hidden', zIndex: 10 }} />
            <svg style={{ width: '100%', height: '300px' }} ref={svgRef} />
          </div>
        )
      ) : (
        <Row justify='center' align='middle' style={{ height: 200 }}>
          <h2>Data not available</h2>
        </Row>
      )}
    </>
  )
}

const mapStateToProps = (state) => ({ patient: state.patient.patient })
const mapDispatchToProps = () => ({})
export default connect(mapStateToProps, mapDispatchToProps)(TracPatchExerciseRepsChart)
